import { dayjs } from '@mobi/utils/date'
import type { KambiBetOffer, KambiEvent } from '@mobi/api-types/kambi'
import type {
  KambiSportsMultiSelection,
  KambiSportsSameGameMultiSelection,
  KambiSportsSelection,
  KambiSportsSingleSelection,
} from '@mobi/betslip/types'
import type { QuickbetSelection } from '@core/Areas/Quickbet/types'
import type { KambiSportBetDetails } from '@core/Areas/ShareBet/LoadBet/types'
import {
  getKambiBetOffersByIds,
  getKambiEventOutcomePricing,
} from '@core/Areas/KambiSports/helpers/api/offering'

export const buildKambiSportsBet = async ({
  betSelectionType,
  betOfferIds,
  outcomeIds,
  outcomesWithEarlyPayout,
}: KambiSportBetDetails): Promise<QuickbetSelection> => {
  const { betOffers, events } = await getKambiBetOffersByIds({ betOfferIds })
  if (betOffers.length === 0 || events.length === 0) throw new Error('Unable to build sport bet')

  const betOffersWithMatchingOutcomes = betOffers.map(offer => {
    const outcomes = offer.outcomes.filter(outcome =>
      outcomeIds.some(id => (Array.isArray(id) ? id.includes(outcome.id) : id === outcome.id))
    )
    for (const earlyPayoutOutcomeId of outcomesWithEarlyPayout) {
      const outcome = outcomes.find(x => x.id === earlyPayoutOutcomeId)
      if (!outcome) {
        continue
      }
      outcome.outcomeTags ||= []
      outcome.outcomeTags.push('ELIGIBLE_FOR_EARLY_SETTLEMENT')
    }
    return { ...offer, outcomes }
  })

  const selection = await mapBetOffersToSelection({
    betSelectionType,
    outcomeIds,
    events,
    betOffers: betOffersWithMatchingOutcomes,
  })
  if (!selection) throw new Error('Unable to build sport bet')

  return {
    bettingType: 'kambi-sports',
    selection,
    selectionDetails: { detailsType: 'kambi', events },
    isEachWayAvailable: false,
    shouldAllowPlaceInvestment: false,
  }
}

// =============
// Local Helpers
// =============

async function mapBetOffersToSelection({
  betSelectionType,
  outcomeIds,
  events,
  betOffers,
}: {
  betSelectionType: KambiSportBetDetails['betSelectionType']
  outcomeIds: KambiSportBetDetails['outcomeIds']
  events: KambiEvent[]
  betOffers: KambiBetOffer[]
}): Promise<KambiSportsSelection | null> {
  if (betSelectionType === 'single') {
    const selection: KambiSportsSingleSelection = {
      betSelectionType,
      betOffer: betOffers[0],
    }
    return selection
  }

  if (betSelectionType === 'same-game-multi') {
    const pricingResponse = await getKambiEventOutcomePricing({
      eventId: events[0].id,
      outcomeIds: outcomeIds.flat(),
    })
    const selection: KambiSportsSameGameMultiSelection = {
      betSelectionType,
      betOffers,
      odds: pricingResponse.selectedOdds.decimal,
    }
    return selection
  }

  if (betSelectionType === 'multi' && betOffers.length > 1) {
    const legs = await Promise.all(
      outcomeIds.map(async outcomeId => {
        // ===================
        // Same Game Multi Leg
        // ===================
        if (Array.isArray(outcomeId)) {
          const betOffersForSGM = betOffers.filter(offer =>
            offer.outcomes.some(({ id }) => outcomeId.includes(id))
          )
          const pricingResponse = await getKambiEventOutcomePricing({
            eventId: betOffersForSGM[0].eventId,
            outcomeIds: outcomeId.flat(),
          })
          const selection: KambiSportsSameGameMultiSelection = {
            betSelectionType: 'same-game-multi',
            betOffers: betOffersForSGM,
            odds: pricingResponse.selectedOdds.decimal,
          }
          return selection
        }

        // =========
        // Singe Leg
        // =========

        const betOffer = betOffers.find(offer => offer.outcomes.some(({ id }) => id === outcomeId))

        const event = events.find(e => e.id === betOffer?.eventId)

        if (
          !betOffer ||
          !event ||
          event.state !== 'NOT_STARTED' ||
          dayjs().isAfter(betOffer.closed)
        ) {
          return null
        }
        const selection: KambiSportsSingleSelection = {
          betSelectionType: 'single',
          betOffer,
        }
        return selection
      })
    )

    const validLegs = legs.filter(l => l !== null)
    if (validLegs.length === 0) {
      return null
    }

    if (validLegs.length === 1) {
      return {
        betSelectionType: 'single',
        betOffer: (validLegs[0] as KambiSportsSingleSelection).betOffer,
      }
    }

    const selection: KambiSportsMultiSelection = {
      betSelectionType,
      legs: validLegs,
    }

    return selection
  }

  if (betSelectionType === 'multi' && betOffers.length === 1) {
    const betOffer = betOffers[0]
    const outcomes = betOffer.outcomes.filter(outcome => outcomeIds.includes(outcome.id))
    const selection: KambiSportsSingleSelection = {
      betSelectionType: 'single',
      betOffer: { ...betOffer, outcomes },
    }
    return selection
  }

  return null
}
